import { useMemo, useState } from 'react';

export interface UseMultiSelectOptions<T> {
  selectedValues: T[];
  onValueChange: (items: T[]) => void;
  getItemValue: (item: T) => string;
  disabled: boolean;
}

export interface UseMultiSelectReturn<T> {
  selectedIds: ReadonlySet<string>;
  resetKey: number;
  handleSelect: (item: T) => void;
  handleRemove: (itemValue: string) => void;
}

export function useMultiSelect<T>({
  selectedValues,
  onValueChange,
  getItemValue,
  disabled,
}: UseMultiSelectOptions<T>): UseMultiSelectReturn<T> {
  const [resetKey, setResetKey] = useState(0);

  const selectedIds = useMemo(
    () => new Set(selectedValues.map((item) => getItemValue(item))),
    [selectedValues, getItemValue],
  );

  const handleSelect = (item: T) => {
    if (disabled) {
      return;
    }

    const itemValue = getItemValue(item);

    if (selectedIds.has(itemValue)) {
      return;
    }

    onValueChange([...selectedValues, item]);
    setResetKey((prev) => prev + 1);
  };

  const handleRemove = (itemValue: string) => {
    if (disabled) {
      return;
    }

    onValueChange(
      selectedValues.filter((item) => getItemValue(item) !== itemValue),
    );
  };

  return { selectedIds, resetKey, handleSelect, handleRemove };
}
